
import { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { getEdgeFunctionUrl } from '@/utils/supabaseUrl';

interface FormData {
  marketType: string;
  productName: string;
  price: string;
  location: string;
  productRemark: string;
}

export const useDataEntryForm = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    marketType: '',
    productName: '',
    price: '',
    location: '',
    productRemark: ''
  });
  const [date, setDate] = useState<Date>(new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      // Reset location when market type changes
      if (field === 'marketType' && value !== prev.marketType) {
        newData.location = '';
      }
      return newData;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('=== DATA ENTRY FORM SUBMISSION START ===');
    console.log('Current user context:', { 
      userId: user?.id, 
      email: user?.email, 
      role: user?.role 
    });

    if (!user?.id) {
      console.error('ERROR: No user ID available for data entry');
      toast.error('User not authenticated - cannot track data entry');
      return;
    }

    if (!formData.marketType || !formData.productName || !formData.price || !formData.location) {
      toast.error('Please fill in all required fields');
      return;
    }

    const priceValue = parseFloat(formData.price);
    if (priceValue <= 0) {
      toast.error('Price must be greater than 0');
      return;
    }

    setIsSubmitting(true);

    try {
      const submissionData = {
        market_type: formData.marketType,
        product_name: formData.productName,
        price: priceValue,
        location: formData.location,
        date: date.toISOString().split('T')[0],
        product_remark: formData.productRemark || '',
        created_by: user.id // Explicitly set the user ID
      };

      console.log('Submitting price data with user tracking:', submissionData);

      // Use the same endpoint as the Telegram webapp but with explicit tracking
      const response = await fetch(`${getEdgeFunctionUrl('benchmark-webview')}?user_id=${user.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      const result = await response.json();
      
      console.log('Submission response:', { 
        status: response.status, 
        success: result.success, 
        message: result.message,
        error: result.error 
      });

      if (response.ok && result.success) {
        toast.success(result.message || 'Price entry saved successfully!');
        console.log('✓ Price entry submitted successfully with user tracking');
        
        // Reset form
        setFormData({
          marketType: '',
          productName: '',
          price: '',
          location: '',
          productRemark: ''
        });
        setDate(new Date());
      } else {
        console.error('Submission failed:', result);
        toast.error(result.error || 'Error saving entry. Please try again.');
      }
    } catch (error) {
      console.error('Network error during submission:', error);
      toast.error('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
      console.log('=== DATA ENTRY FORM SUBMISSION END ===');
    }
  };

  return {
    formData,
    date,
    isSubmitting,
    setDate,
    handleInputChange,
    handleSubmit
  };
};
